<template>
  <footer class="footer" id="contact-us">
    <!-- Contact Us -->
    <section class="section my-0 overflow-hidden">
      <div class="container pb-300">
        <div class="row text-center justify-content-center">
          <div class="col-lg-10">
            <h2 class="display-3">Contact Us</h2>
            <p class="mt-4 lead">
              If you have any inquiries, you may reach out to us via email at
              <a href="mailto:obcmstaana@gmail.com">obcmstaana@gmail.com</a> or send us a
              message using this form.
            </p>
          </div>
        </div>
      </div>
    </section>
    <section class="section section-lg pt-lg-0 section-contact-us">
      <div class="container">
        <div class="row justify-content-center mt--300">
          <div class="col-lg-8">
            <card gradient="secondary" shadow body-classes="p-lg-5">
              <h4 class="mb-1 text-primary">Want to connect with us?</h4>
              <p class="mt-0">Feel free to message us</p>

              <loading
                :active.sync="isLoading"
                loader="spinner"
                color="#20A8D8"
                :is-full-page="false"
              />
              <b-form @submit.prevent="onSubmit" novalidate>
                <b-form-group label="Name" label-for="Name">
                  <b-input-group class="mb-2 mr-sm-2 mb-sm-0">
                    <b-form-input
                      name="Name"
                      placeholder="Name"
                      maxlength="100"
                      v-model="form.name"
                      v-validate="{
                        required: true,
                        regex: nameRegex,
                      }"
                      autocomplete="off"
                    />
                  </b-input-group>
                  <span v-if="errors.has('Name')" class="help-block">
                    {{ errors.first("Name") }}
                  </span>
                </b-form-group>

                <b-form-group label="Email" label-for="Email">
                  <b-input-group class="mb-2 mr-sm-2 mb-sm-0">
                    <b-form-input
                      name="Email"
                      placeholder="Email"
                      v-model="form.email"
                      v-validate="'required|email'"
                      autocomplete="off"
                    />
                  </b-input-group>
                  <span v-if="errors.has('Email')" class="help-block">
                    {{ errors.first("Email") }}
                  </span>
                </b-form-group>

                <b-form-group label="Contact No." label-for="Contact No.">
                  <b-input-group class="mb-2 mr-sm-2 mb-sm-0">
                    <b-form-input
                      name="Contact No."
                      placeholder="+639xxxxxxxxx | (XX) XXXX XXXX"
                      maxlength="50"
                      v-model="form.contactNo"
                      v-validate="{
                        required: true,
                        regex: contactNoRegex,
                      }"
                      autocomplete="off"
                    />
                  </b-input-group>
                  <span v-if="errors.has('Contact No.')" class="help-block">
                    {{ "Allowed format: +639xxxxxxxxx or (XX) XXXX XXXX" }}
                  </span>
                </b-form-group>

                <b-form-group label="Message" label-for="Message">
                  <b-form-textarea
                    name="Message"
                    v-model="form.message"
                    v-validate="{
                      required: true,
                      regex: messageRegex,
                    }"
                    class="form-control form-control-alternative mt-2"
                    rows="4"
                    cols="80"
                    placeholder="Type a message..."
                  ></b-form-textarea>
                  <span v-if="errors.has('Message')" class="help-block">
                    {{ "Message must be no shorter than 30 characters and no longer than 1000 characters." }}
                  </span>
                </b-form-group>

                <b-row class="mt-2">
                  <b-col class="text-center">
                    <b-button
                      type="button"
                      variant="secondary"
                      @click="onReset"
                      round
                      block
                      size="lg"
                      :disabled="disableConfirmButtons"
                    >
                      Reset
                    </b-button>
                  </b-col>
                  <b-col class="text-center">
                    <b-button
                      type="submit"
                      variant="primary"
                      round
                      block
                      size="lg"
                      :disabled="disableConfirmButtons"
                    >
                      Send Message
                    </b-button>
                  </b-col>
                </b-row>
              </b-form>
            </card>
          </div>
        </div>
      </div>
    </section>

    <div class="container">
      <hr />
      <div class="row align-items-start justify-content-md-between mb-4">
        <div class="col-sm-3 col-md-4 mt-1">
          <div class="copyright">
            &copy; {{ year }}
            <a href="/" rel="noopener">OBCM</a>
          </div>
        </div>

        <div class="col-sm-9 col-md-8 d-block d-lg-none">
          <div>
            <a href="/what-we-teach" class="nav-link" rel="noopener">What We Teach</a>
          </div>
          <div class="mt-2">
            <a href="/our-ministries" class="nav-link" rel="noopener">Our Ministries</a>
          </div>
          <div class="mt-2">
            <a href="/our-leaders" class="nav-link" rel="noopener">Our Leaders</a>
          </div>
          <div class="mt-2">
            <a href="/church-planting" class="nav-link" rel="noopener">Church Planting</a>
          </div>
          <div class="mt-2">
            <a href="/affiliates" class="nav-link" rel="noopener">Affiliates</a>
          </div>
          <div class="mt-2">
            <a href="/giving" class="nav-link" rel="noopener">Giving</a>
          </div>
        </div>

        <div class="col-sm-9 col-md-8 d-none d-lg-block">
          <ul class="nav nav-footer justify-content-end">
            <li class="nav-item text-right">
              <a href="/what-we-teach" class="nav-link" rel="noopener">What We Teach</a>
            </li>
            <li class="nav-item text-right">
              <a href="/our-ministries" class="nav-link" rel="noopener">Our Ministries</a>
            </li>
            <li class="nav-item text-right">
              <a href="/our-leaders" class="nav-link" rel="noopener">Our Leaders</a>
            </li>
            <li class="nav-item text-right">
              <a href="/church-planting" class="nav-link" rel="noopener">Church Planting</a>
            </li>
            <li class="nav-item text-right">
              <a href="/affiliates" class="nav-link" rel="noopener">Affiliates</a>
            </li>
            <li class="nav-item text-right">
              <a href="/giving" class="nav-link" rel="noopener">Giving</a>
            </li>
          </ul>
        </div>
      </div>

      <div>
        <button v-show="showButton" @click="scrollToTop" class="back-to-top">
          ↑ BACK TO TOP
        </button>
      </div>
    </div>
  </footer>
</template>

<script>
import api from "@/database/inquiry.js";
import config from "@/config/env-constants";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  name: "app-footer",
  components: {
    Loading,
  },
  data() {
    return {
      year: new Date().getFullYear(),
      form: {
        name: "",
        email: "",
        contactNo: "",
        message: "",
        source: "obcm.ph",
        status: "PENDING",
        dateCreated: null,
      },

      // Check for loader
      isLoading: false,

      // Back to top button
      showButton: false,
    };
  },
  computed: {
    disableConfirmButtons() {
      return this.isLoading;
    },
    nameRegex() {
      return config.nameRegex;
    },
    contactNoRegex() {
      return config.contactNoRegex;
    },
    messageRegex() {
      return config.messageRegex;
    },
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);

    // reset form values
    this.onReset();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.showButton = window.scrollY > 300;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    async onSubmit(event) {
      event.preventDefault();

      try {
        this.isLoading = true;

        let isValid = await this.$validator.validateAll();
        if (!isValid) {
          this.isLoading = false;
          return;
        }

        let inquiry = Object.assign({}, this.form);
        inquiry.dateCreated = new Date().getTime();

        let result = await api.addInquiry(inquiry);
        if (result && !_.isEmpty(result)) {
          this.$toaster.success(
            "Your inquiry was successfully sent. Please wait for reply from our admin representative."
          );
          this.onReset();
        } else {
          this.$toaster.error(
            "There is a problem sending your inquiry. Please try again later."
          );
        }
      } catch (err) {
        console.log(err);
      }
    },
    onReset() {
      // Reset our form values
      this.form.name = "";
      this.form.email = "";
      this.form.contactNo = "";
      this.form.message = "";

      // reset validation
      this.$validator.reset();
      this.errors.clear();
      this.isLoading = false;

      // Trick to reset/clear native browser form validation state
      this.show = false;
      this.$nextTick(() => {
        this.show = true;
      });
    },
  },
};
</script>

<style scoped>
.back-to-top {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #5d71e3;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  box-shadow: 0 2px 5px rgba(93, 113, 227);
  transition: opacity 0.3s;
}

.back-to-top:hover,
.back-to-top:focus {
  background: #5d71e3 !important;
  box-shadow: 0 4px 10px rgba(93, 113, 227);
  border: none;
  outline: none;
}
</style>
